import React, {Fragment, useState} from 'react';
import { Link } from "react-router-dom";
import _ from 'lodash';
import Comment from './Comment';
import {PageLoader, SpinLoader} from "../Loader";
import createLink from "../../../libs/createLink";
import CommentEditor from "./components/CommentEditor/CommentEditor";
import {Can} from "../../../ability-context";
import {useInfiniteQuery, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
    fetchUserFriends,
    USER_CURRENT_KEY, USER_FRIENDS_KEY
} from "../../../api/public-voissa/users";
import {
    COMMENTS_KEY, createComment, createSubComment,
    deleteComment, deleteSubComment,
    fetchComments,
    reportComment, reportSubComment, updateComment, updateSubComment
} from "../../../api/public-voissa/comments";
import {toastError, toastSuccess} from "../../../libs/toast";
import {COMMENT} from "../../../const/toasts";
import EncourageSignup from "../../components2/encourageSignup/EncourageSignup";


const initialSubCommentAnswer = {
    owner: {},
    commentId: ""
};

const Comments = ({
        parent = {},
        commentsAllowed = false,
        type = "",
        sort = "-createdAt",
        limit = 10,
        previewSubComment = 0,
        className = "column",
        onFocus = () =>{},
        onBlur = () =>{}
    }) =>{
    const queryClient = useQueryClient();

    const filter = {
        page: 1,
        limit,
        sort
    };

    const dataCurrentUser = queryClient.getQueryData([USER_CURRENT_KEY]);
    const currentUserId = _.get(dataCurrentUser, '_id');
    const isCurrentUser = !!currentUserId;

    const [subCommentAnswer, setSubCommentAnswer] = useState(initialSubCommentAnswer);
    const [currentCommentEdit, setCurrentCommentEdit] = useState({});
    const [toggleSignIn, setToggleSignIn] = useState(false);


    const {data : dataFriends} = useQuery(
        [USER_FRIENDS_KEY, currentUserId, {limit: false}],
        ()=> fetchUserFriends(currentUserId, {limit: false}),
        {
            staleTime : (1000 * 60 * 5),
            enabled : !!isCurrentUser,
            placeholderData : {
                data : [],
                total : 0
            },
            select : (data) =>{
                return {
                    friends : data.data || [],
                    total : data.total
                }
            }
        }
    );

    let {friends = []} = dataFriends || {};

    const {isSuccess : isSuccessComments, isFetched : isFetchedComments, data : dataComments, fetchNextPage : fetchNextPageComments, hasNextPage : hasNextPageComments} = useInfiniteQuery(
        [COMMENTS_KEY, parent._id],
        ({pageParam = 1}) => fetchComments(type, parent._id, {...filter, page : pageParam}),
        {
            enabled : !!type && !!parent._id,
            getNextPageParam: (lastPage, pages) => lastPage && lastPage.more ? pages.length +1 : undefined,
        }
    );

    const {
        mutate : mutateCreateComment,
    } = useMutation((data) => createComment(type, parent._id, data),{
        onSuccess: async (data) =>{
            const previousValue = queryClient.getQueryData([COMMENTS_KEY, parent._id]);
            if(previousValue){
                let newPagesArray = previousValue?.pages || [];
                if(_.get(newPagesArray, '[0].data')){
                    newPagesArray[0].data = [...newPagesArray[0].data,data];
                    newPagesArray[0].total = newPagesArray[0].total +1;
                }
                queryClient.setQueryData([COMMENTS_KEY, parent._id], oldData => ({
                    pages: newPagesArray,
                    pageParams: oldData.pageParams,
                }));
            }
        },
        onError: () =>{
            toastError(COMMENT.ERROR.CREATE);
        }
    });

    const {
        mutate : mutateCreateSubComment,
    } = useMutation((comment) => createSubComment(type, parent._id, comment.parentCommentId, comment.data),{
        onSuccess: async (data) =>{
            let commentParentId = data.parentId;
            const previousValue = queryClient.getQueryData([COMMENTS_KEY, parent._id, commentParentId]);
            //si il y a déjà des subCommentaires on met a jour
            if(previousValue){
                let newPagesArray = previousValue?.pages || [];
                if(_.get(newPagesArray, '[0].data')){
                    newPagesArray[0].data = [...newPagesArray[0].data,data];
                    newPagesArray[0].total = newPagesArray[0].total +1;
                }
                queryClient.setQueryData([COMMENTS_KEY, parent._id, commentParentId], oldData => ({
                    pages: newPagesArray,
                    pageParams: oldData.pageParams,
                }));
            }
            //sinon on update le parent pour prevenir qu'il y a des subCommentaires
            else{
                const previousValue = queryClient.getQueryData([COMMENTS_KEY, parent._id]);
                if(previousValue){
                    const newPagesArray = previousValue?.pages.map((page) => {
                            page.data = page.data.map((item) => {
                                if(item._id.toString() === commentParentId.toString()){
                                    item = {...item, subComments : true};
                                }
                                return item;
                            });
                            return page;
                        }
                    ) ?? []
                    queryClient.setQueryData([COMMENTS_KEY, parent._id], oldData => ({
                        pages: newPagesArray,
                        pageParams: oldData.pageParams,
                    }));
                }
            }

        },
        onError: () =>{
            toastError(COMMENT.ERROR.CREATE);
        }
    });

    const {
        mutate : mutateUpdateComment,
    } = useMutation((comment) => updateComment(type, parent._id, comment.id, comment.data),{
        onSuccess: async (data) =>{
            const previousValue = queryClient.getQueryData([COMMENTS_KEY, parent._id]);
            if(previousValue){
                const newPagesArray = previousValue?.pages.map((page) => {
                        page.data = page.data.map((item) => {
                            if(item._id.toString() === data._id.toString()){
                                item = {...item, ...data};
                            }
                            return item;
                        });
                        return page;
                    }
                ) ?? []

                queryClient.setQueryData([COMMENTS_KEY, parent._id], oldData => ({
                    pages: newPagesArray,
                    pageParams: oldData.pageParams,
                }));
            }

        },
        onError: () =>{
            toastError(COMMENT.ERROR.UPDATE);
        }
    });

    const {
        mutate : mutateUpdateSubComment,
    } = useMutation((comment) => updateSubComment(type, parent._id, comment.parentCommentId, comment.id, comment.data),{
        onSuccess: async (data) =>{
            let commentParentId = data.parentId;
            const previousValue = queryClient.getQueryData([COMMENTS_KEY, parent._id, commentParentId]);
            if(previousValue){
                const newPagesArray = previousValue?.pages.map((page) => {
                        page.data = page.data.map((item) => {
                            if(item._id.toString() === data._id.toString()){
                                item = {...item, ...data};
                            }
                            return item;
                        });
                        return page;
                    }
                ) ?? []

                queryClient.setQueryData([COMMENTS_KEY, parent._id, commentParentId], oldData => ({
                    pages: newPagesArray,
                    pageParams: oldData.pageParams,
                }));
            }
        },
        onError: () =>{
            toastError(COMMENT.ERROR.UPDATE);
        }
    });


    const {
        mutate : mutateDeleteComment,
    } = useMutation((commentId) => deleteComment(type, parent._id, commentId),{
        onSuccess: async (data) =>{
            const previousValue = queryClient.getQueryData([COMMENTS_KEY, parent._id]);
            if(previousValue){
                const newPagesArray = previousValue?.pages.map((page) => {
                        page.data = page.data.filter((item) => item._id.toString() !== data._id.toString());
                        page.total = page.total - 1;
                        return page;
                    }
                ) ?? []

                queryClient.setQueryData([COMMENTS_KEY, parent._id], oldData => ({
                    pages: newPagesArray,
                    pageParams: oldData.pageParams,
                }));
            }

        },
        onError: () =>{
            toastError(COMMENT.ERROR.DELETE);
        }
    });

    const {
        mutate : mutateDeleteSubComment,
    } = useMutation((data) => deleteSubComment(type, parent._id, data.commentId, data.subCommentId),{
        onSuccess: async (data) =>{
            let commentParentId = data.parentId;
            const previousValue = queryClient.getQueryData([COMMENTS_KEY, parent._id, commentParentId]);
            if(previousValue){
                const newPagesArray = previousValue?.pages.map((page) => {
                        page.data = page.data.filter((item) => item._id.toString() !== data._id.toString());
                        page.total = page.total - 1;
                        return page;
                    }
                ) ?? []

                queryClient.setQueryData([COMMENTS_KEY, parent._id, commentParentId], oldData => ({
                    pages: newPagesArray,
                    pageParams: oldData.pageParams,
                }));
            }

        },
        onError: () =>{
            toastError(COMMENT.ERROR.DELETE);
        }
    });


    const {
        mutate : mutateReportComment,
    } = useMutation(data => reportComment(type, parent._id, data.id, {message : data.message}),{
        onSuccess: () =>{
            toastSuccess(COMMENT.SUCCESS.REPORT);
        },
        onError: () =>{
            toastError(COMMENT.ERROR.REPORT);
        }
    });

    const {
        mutate : mutateReportSubComment,
    } = useMutation(data => reportSubComment(type, parent._id, data.commentId, data.subCommentId, {message : data.message}),{
        onSuccess: () =>{
            toastSuccess(COMMENT.SUCCESS.REPORT);
        },
        onError: () =>{
            toastError(COMMENT.ERROR.REPORT);
        }
    });

    function handleSubmit(value) {
        if(subCommentAnswer.commentId){
            mutateCreateSubComment({parentCommentId : subCommentAnswer.commentId,  data :value});
            scrollToBottom(`comment_${subCommentAnswer.commentId}`)

        } else {
            mutateCreateComment(value);
            setSubCommentAnswer(initialSubCommentAnswer);
            scrollToBottom(`comments`)
        }
        setSubCommentAnswer(initialSubCommentAnswer);
    }

    function handleSubmitEdit({
            parentCommentId,
            commentId,
        }, contentState) {
        if (parentCommentId) {
            mutateUpdateSubComment({id : commentId, parentCommentId, data : contentState});

        }
        else {
            mutateUpdateComment({id : commentId, data : contentState});
        }
        setCurrentCommentEdit({});

    }

    function updateSort(data){
        let pages = data.map((page, index) => {
            page.data = _.orderBy(page.data, ['createdAt'], ['desc']);
            page.pageNumber = index;
            return page;
        } )
        pages = _.orderBy(pages, ['pageNumber'], ['asc']);
        return pages;
    }


    let canOptions = {
        subject: parent,
        modelName: "Comment",
        action: "create",
        passThrough: true
    };

    let total = _.get(dataComments,'pages[0].total') || 0;

    let data = [];
    if(_.get(dataComments,'pages')){
        data = updateSort(dataComments.pages);
    }

    function scrollToBottom(id) {
        let elementComments = document.getElementById('comments');
        if(id === "comments"){
            const offset = 50;
            const bodyRect = document.body.getBoundingClientRect().top;
            const elementRect = elementComments.getBoundingClientRect().top;
            const elementPosition = elementRect - bodyRect;
            const offsetPosition = elementPosition - offset;
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
            elementComments.scrollIntoView({behavior: 'smooth'}, true);
        }
        else{
            let element = document.getElementById(id);
            if (element) {
                const offset = 50;
                const bodyRect = elementComments.getBoundingClientRect().top;
                const elementRect = element.getBoundingClientRect().top;
                const elementPosition = elementRect - bodyRect;
                const offsetPosition = elementPosition - offset;
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
                element.scrollIntoView({behavior: 'smooth'}, true);
            }
        }
    }


    return (
        <div className={`${className}`}>
            <div className="comments">
                <div className="comments-list" id={"comments"}>
                    {!isSuccessComments ? <PageLoader /> : null}
                    {isSuccessComments && total === 0 ? (
                        <div className={"no-content"}>Aucun commentaire</div>
                    ) : null}
                    {isSuccessComments && data.map((group, i) => (
                        <Fragment key={i}>
                            {group.data.map(comment => (
                                <Comment
                                    key={comment._id}
                                    comment={comment}
                                    setCurrentCommentEdit={(data) => setCurrentCommentEdit(data)}
                                    currentUser={dataCurrentUser}
                                    parentId={parent._id}
                                    commentsInitialized={isSuccessComments}
                                    deleteComment={(commentId) => mutateDeleteComment(commentId)}
                                    deleteSubComment={(commentId, subCommentId)=>mutateDeleteSubComment({commentId, subCommentId})}
                                    handleReport={(commentId, message) =>mutateReportComment({id : commentId, message})}
                                    handleSubReport={(commentId, subCommentId, message) =>mutateReportSubComment({id : commentId, subCommentId, message})}
                                    subCommentAnswer={(commentId, ownerOfSubComment) => setSubCommentAnswer({owner: ownerOfSubComment, commentId})}
                                    commentType={type}
                                    hasSubComments={comment.subComments}
                                    setToggleSignIn={(status) => setToggleSignIn(status)}
                                    previewSubComment={previewSubComment}
                                />
                            ))}
                        </Fragment>
                    ))}
                    {hasNextPageComments && (
                        <div className={`more-comments ${!isFetchedComments ? "loading" :""}`} onClick={() => fetchNextPageComments()}>
                            {isFetchedComments ? "Voir plus de commentaires" : <SpinLoader className={"small"}/>}
                        </div>
                    )}
                </div>
                {commentsAllowed ? (
                    <Can {...canOptions}>
                        {can=>(
                            <CommentEditor
                                mentions={friends}
                                onSubmit={handleSubmit}
                                onSubmitEdit={handleSubmitEdit}
                                onCancelRespond={()=>{
                                    setSubCommentAnswer(initialSubCommentAnswer);
                                }}
                                onCancelEdit={()=>{
                                    setCurrentCommentEdit({})
                                }}
                                onBlur={()=>onBlur()}
                                onFocus={()=>onFocus()}
                                edit={currentCommentEdit}
                                respondMention={subCommentAnswer.owner}
                                cannot={!can}
                            />)}
                    </Can>
                ) : (
                    <div className="close-comments">
                        {isCurrentUser ? "Commentaires fermés par l’auteur" : <span>Veuillez vous <Link
                            to={createLink("signin")}>connecter</Link> pour publier un commentaire</span>}
                    </div>
                )}
            </div>
            <EncourageSignup toggle={toggleSignIn} handleClose={() => setToggleSignIn(false)} />
        </div>
    )
};

export default Comments;